<template>
  <div class="dashboard-main-content py-3 pt-4 p-md-5">

    <!-- ── Greeting ─────────────────────────────────────────── -->
    <h2 class="mb-1 text-primary-emphasis">
      Olá, <span class="fw-bold">{{ userName }}</span> 👋
    </h2>
    <p class="text-muted mb-4 context-message">{{ contextMessage }}</p>

    <!-- ── Banner de celebração (onboarding completo) ─────────── -->
    <div v-if="showOnboardingComplete" class="onboarding-complete-banner mb-4">
      <div class="onboarding-complete-banner__icon">🎉</div>
      <div class="onboarding-complete-banner__content">
        <p class="onboarding-complete-banner__title">Cadastro completo!</p>
        <p class="onboarding-complete-banner__subtitle">
          Agora você pode criar sua primeira lista e começar a receber presentes.
        </p>
      </div>
      <router-link to="/my-account/add" class="onboarding-complete-banner__cta">
        Criar lista agora
      </router-link>
    </div>

    <!-- ── Onboarding banners ────────────────────────────────── -->
    <div
      v-if="profileSynced && onboardingWarning"
      :class="['onboarding-banner mb-4', onboardingBannerClass]"
      @click="openPlatformOnboarding"
      role="button"
    >
      <div class="onboarding-banner__icon"><i class="fas fa-user-edit"></i></div>
      <div class="onboarding-banner__content">
        <p class="onboarding-banner__title">{{ onboardingWarning.title }}</p>
        <p class="onboarding-banner__subtitle">{{ onboardingWarning.subtitle }}</p>
      </div>
      <i class="fas fa-chevron-right onboarding-banner__arrow"></i>
    </div>

    <!-- Tour help button -->
    <div class="d-flex justify-content-end mb-2">
      <button class="dash-tour-btn" @click="startTour" title="Abrir guia da tela">
        <i class="fas fa-circle-question me-1"></i>Ver guia
      </button>
    </div>

    <!-- ── Stat cards ────────────────────────────────────────── -->
    <div class="row g-3 mb-4" data-tour="dash-stats">

      <!-- COM DRAFT: substitui os 2 cards -->
      <template v-if="hasDraft">

        <div class="col-6">
          <div class="stat-card stat-card--draft">
            <div class="stat-card__icon stat-card__icon--gray">
              <i class="fas fa-lock"></i>
            </div>
            <div class="stat-card__body">
              <p class="stat-card__label">Pendentes</p>
              <p class="stat-card__value">{{ draftEvents.length }}</p>
            </div>
          </div>
        </div>

        <div class="col-6">
          <div class="stat-card">
            <div class="stat-card__icon stat-card__icon--purple">
              <i class="fas fa-calendar-check"></i>
            </div>
            <div class="stat-card__body">
              <p class="stat-card__label">Ativos</p>
              <p class="stat-card__value">{{ inProgressEvents.length }}</p>
            </div>
          </div>
        </div>

      </template>

      <!-- SEM DRAFT: exibe os cards originais -->
      <template v-else>

        <div class="col-6 stat-card-col--receive">
          <div class="stat-card" @click="$router.push('/finance')">
            <div class="stat-card__icon stat-card__icon--green">
              <i class="fas fa-coins"></i>
            </div>
            <div class="stat-card__body">
              <p class="stat-card__label" title="Total arrecadado pelos seus eventos (bruto)">Arrecadado</p>
              <p class="stat-card__value">
                <span v-if="isBalanceHidden">••••</span>
                <span v-else-if="loadingStats" class="placeholder-glow"><span class="placeholder col-8"></span></span>
                <span v-else-if="totalPaid <= 0" class="stat-card__value--empty">
                  —
                </span>
                <span v-else>{{ formatCurrency(totalPaid) }}</span>
              </p>
            </div>
            <button class="stat-card__toggle" @click.stop="toggleBalance" aria-label="Alternar visibilidade do saldo">
              <i :class="['fas', isBalanceHidden ? 'fa-eye-slash' : 'fa-eye']"></i>
            </button>
          </div>
        </div>

        <div class="col-6 stat-card-col--active">
          <div class="stat-card" @click="$router.push('/my-account/events')">
            <div class="stat-card__icon stat-card__icon--purple">
              <i class="fas fa-calendar-check"></i>
            </div>
            <div class="stat-card__body">
              <p class="stat-card__label">Eventos ativos</p>
              <p class="stat-card__value">
                <span v-if="loadingStats" class="placeholder-glow"><span class="placeholder col-4"></span></span>
                <span v-else>{{ inProgressEvents.length }}</span>
              </p>
            </div>
          </div>
        </div>

      </template>

    </div>

    <!-- ── Ações principais ──────────────────────────────────── -->
    <div class="row g-2 mb-4" data-tour="dash-actions">
      <div class="col-6">
        <router-link to="/my-account/add" class="action-btn action-btn--primary">
          <i class="fas fa-plus-circle"></i>
          <span>Criar Evento</span>
        </router-link>
      </div>
      <div class="col-6">
        <router-link to="/my-account/events" class="action-btn action-btn--secondary">
          <i class="fas fa-calendar-alt"></i>
          <span>Meus Eventos</span>
        </router-link>
      </div>
    </div>

    <!-- ── Eventos pendentes (draft) ────────────────────────── -->
    <div v-if="draftEvents.length > 0" class="mb-4">
      <h6 class="section-label mb-2">
        <i class="fas fa-lock me-1 text-muted"></i> Eventos pendentes
      </h6>
      <div
        v-for="event in draftEvents"
        :key="event.id"
        class="event-mini-card event-card--draft"
        @click="openDraftModal(event)"
        role="button"
      >
        <div class="event-mini-card__thumb">
          <img v-if="event.image" :src="event.image" :alt="event.name" />
          <div v-else class="event-mini-card__thumb-placeholder">
            <i class="fas fa-gift"></i>
          </div>
        </div>
        <div class="event-mini-card__info">
          <p class="event-mini-card__name">
            <span v-if="event.is_premium" class="event-premium-crown" title="Evento Premium"><i class="fas fa-crown"></i></span>
            {{ event.name }}
          </p>
          <span class="event-card__badge">
            <i class="fas fa-lock me-1"></i> Pendente
          </span>
        </div>
        <i class="fas fa-chevron-right event-mini-card__arrow text-muted"></i>
      </div>
    </div>

    <!-- ── Eventos recentes ──────────────────────────────────── -->
    <div class="mb-4" data-tour="dash-events" v-if="!loadingStats && inProgressEvents.length > 0">
      <div class="section-header mb-2">
        <p class="section-title">Eventos ativos</p>
        <router-link to="/my-account/events" class="section-link">Ver todos</router-link>
      </div>

      <div
        class="event-mini-card"
        v-for="event in inProgressEvents.slice(0, 3)"
        :key="event.id"
        @click="openActiveEventModal(event)"
        role="button"
      >
        <div class="event-mini-card__thumb">
          <img v-if="event.image" :src="event.image" :alt="event.name" />
          <div v-else class="event-mini-card__thumb-placeholder">
            <i class="fas fa-gift"></i>
          </div>
        </div>
        <div class="event-mini-card__info">
          <p class="event-mini-card__name">
            <span v-if="event.is_premium" class="event-premium-crown" title="Evento Premium"><i class="fas fa-crown"></i></span>
            {{ event.name }}
          </p>
          <div class="event-mini-card__summary">
            <div class="event-mini-card__stats-inline">
              <span class="event-mini-card__meta">
                <i class="fas fa-coins me-1"></i>
                <strong>{{ formatCurrency(event.details?.finalCollected || 0) }}</strong>
              </span>
              <span class="event-mini-card__participants">
                <i class="fas fa-users me-1"></i>
                {{ event.details?.finalParticipants || 0 }} {{ (event.details?.finalParticipants || 0) === 1 ? 'participante' : 'participantes' }}
              </span>
            </div>
          </div>
        </div>
        <i class="fas fa-chevron-right event-mini-card__arrow text-muted"></i>
      </div>
    </div>

    <div v-else-if="!loadingStats" class="empty-events-hint mb-4">
      <i class="fas fa-calendar-plus me-2 text-muted"></i>
      <span class="text-muted small">Nenhum evento ativo ainda.</span>
      <router-link to="/my-account/add" class="ms-2 small fw-semibold text-pink">Criar agora</router-link>
    </div>

    <!-- ── Acesso rápido ─────────────────────────────────────── -->
    <div class="mb-3" data-tour="dash-shortcuts">
      <p class="section-title mb-2">Atalhos</p>
      <div class="row g-2">
        <div class="col-6">
          <router-link to="/my-account/profile" class="quick-card">
            <i class="fas fa-user-circle quick-card__icon"></i>
            <span class="quick-card__label">Meus Dados</span>
            <span v-if="missingProfile" class="quick-card__badge quick-card__badge--warn">!</span>
            <span v-else class="quick-card__badge quick-card__badge--ok">✓</span>
          </router-link>
        </div>
        <div class="col-6">
          <router-link to="/my-account/bank" class="quick-card">
            <i class="fas fa-piggy-bank quick-card__icon"></i>
            <span class="quick-card__label">Dados bancarios</span>
            <span v-if="!authStore.isFinanciallyEnabled" class="quick-card__badge quick-card__badge--warn">!</span>
            <span v-else class="quick-card__badge quick-card__badge--ok">✓</span>
          </router-link>
        </div>
        <div class="col-6">
          <router-link to="/finance" class="quick-card">
            <i class="fas fa-university quick-card__icon"></i>
            <span class="quick-card__label">Minhas Finanças</span>
          </router-link>
        </div>
        <div class="col-6">
          <router-link to="/my-account/security" class="quick-card">
            <i class="fas fa-shield-alt quick-card__icon"></i>
            <span class="quick-card__label">Segurança</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Dúvidas link -->
    <div class="help-hint mt-1 mb-3">
      <i class="fas fa-circle-question me-1"></i>
      Dúvidas?
      <router-link to="/how-it-works" class="help-hint__link">
        Veja como a plataforma funciona →
      </router-link>
    </div>

    <!-- ── Logout ────────────────────────────────────────────── -->
    <div class="mt-4 pt-3 border-top text-center">
      <button class="btn-logout" @click="showLogoutConfirmation = true">
        <i class="fas fa-sign-out-alt me-2"></i>Sair da conta
      </button>
    </div>

    <!-- ── Modal de logout ───────────────────────────────────── -->
    <BottomModal
      :visible="showLogoutConfirmation"
      @close="showLogoutConfirmation = false"
      :fullHeight="false"
    >
      <template #default>
        <div class="confirmation-content p-3 text-center">
          <h5 class="mb-3 text-primary-emphasis">Confirmar Saída?</h5>
          <p class="text-muted mb-4">
            Ao sair, você será desconectado(a). Na sua próxima visita, será necessário informar sua senha para acessar sua conta novamente.
          </p>
          <div class="d-flex justify-content-center gap-3 mt-4">
            <button class="btn btn-sm btn-outline-secondary" @click="showLogoutConfirmation = false">
              <i class="fa-solid fa-chevron-down me-2"></i>
            </button>
            <button class="btn btn-sm btn-color-site" @click="confirmLogout">
              <span v-if="isLoggingOut" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isLoggingOut ? 'Saindo...' : 'Confirmar Saída' }}
            </button>
          </div>
        </div>
      </template>
    </BottomModal>

    <!-- ── Modal: evento ativo ──────────────────────────────── -->
    <EventActionModal
      :visible="showActiveEventModal"
      :event="selectedActiveEvent"
      :isDraft="false"
      :missingProfile="missingProfile"
      :draftActivationMessage="draftActivationMessage"
      :showFinalize="false"
      @close="showActiveEventModal = false"
    />

    <!-- ── Modal: evento draft ──────────────────────────────── -->
    <EventActionModal
      :visible="showDraftModal"
      :event="selectedDraftEvent"
      :isDraft="true"
      :missingProfile="missingProfile"
      :draftActivationMessage="draftActivationMessage"
      :showFinalize="false"
      @close="showDraftModal = false"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { useAuthStore } from '@/stores/authStore';
import api from '@/services/api';
import BottomModal from '@/components/BottomModal.vue';
import EventActionModal from '@/components/EventActionModal.vue';
import { useDashboardTour } from '@/composables/useDashboardTour';

const router    = useRouter();
const authStore = useAuthStore();
const route     = useRoute();

const { startTour, startTourIfFirstVisit } = useDashboardTour();

// ── Estado ────────────────────────────────────────────────────
const showLogoutConfirmation = ref(false);
const isLoggingOut           = ref(false);
const loadingStats           = ref(true);
// Começa false — banner só aparece após o sync de perfil concluir no onMounted.
// Evita flash do warning em usuários com conta já ativa.
const profileSynced          = ref(false);
const totalPaid              = ref(0);
const allEvents              = ref([]);
const isBalanceHidden        = ref(localStorage.getItem('isBalanceHidden') === 'true');

// Separa eventos por status
const inProgressEvents = computed(() => allEvents.value.filter(e => e.status === 'in-progress'));
const draftEvents      = computed(() => allEvents.value.filter(e => e.status === 'draft'));
const hasDraft         = computed(() => draftEvents.value.length > 0);

// Estado do modal draft
const showDraftModal     = ref(false);
const selectedDraftEvent = ref(null);

function openDraftModal(event) {
  selectedDraftEvent.value = event;
  showDraftModal.value     = true;
}

// Estado do modal de evento ativo
const showActiveEventModal  = ref(false);
const selectedActiveEvent   = ref(null);

function openActiveEventModal(event) {
  selectedActiveEvent.value = event;
  showActiveEventModal.value = true;
}

// Banner de celebração após completar onboarding
const showOnboardingComplete = ref(route.query.onboarding === 'complete');
if (showOnboardingComplete.value) {
  setTimeout(() => { showOnboardingComplete.value = false; }, 8000);
}

// ── Computed ──────────────────────────────────────────────────
const userName = computed(() => {
  const fullName = authStore.user?.name;
  return fullName ? fullName.split(' ')[0] : 'usuário';
});

const missingProfile = computed(() => {
  return authStore.normalizedPlatformStatus !== 'active';
});

const draftActivationMessage = computed(() => {
  if (authStore.normalizedPlatformStatus === 'pending') {
    return 'Seu cadastro está em análise. Assim que aprovado, seu evento será liberado automaticamente.';
  }

  return 'Falta concluir seu cadastro. Assim que finalizar, seu evento será liberado automaticamente.';
});

const onboardingWarning = computed(() => {
  const status = authStore.normalizedPlatformStatus;

  if (status === 'active') return null;

  if (status === 'pending') {
    return {
      title: 'Conclua seu cadastro',
      subtitle: 'Você já pode criar seu evento, mas precisa concluir seu cadastro para começar a receber presentes.',
    };
  }

  return {
    title: 'Conclua seu cadastro de recebimento',
    subtitle: 'Falta pouco para receber. Complete seu cadastro para começar.',
  };
});

const onboardingBannerClass = computed(() => {
  if (authStore.normalizedPlatformStatus === 'pending') {
    return 'onboarding-banner--info';
  }

  return 'onboarding-banner--warning';
});

const onboardingProfileLink = computed(() => {
  if (authStore.normalizedPlatformStatus === 'restricted') {
    return '/my-account/profile?ref=reviewaccount';
  }

  return '/my-account/profile?ref=createaccount';
});

const missingPix = computed(() => !authStore.isFinanciallyEnabled);

const contextMessage = computed(() => {
  if (authStore.normalizedPlatformStatus === 'restricted') {
    return 'Falta concluir seu cadastro para liberar os recebimentos. ✨';
  }
  if (authStore.normalizedPlatformStatus === 'pending') {
    return 'Parabéns! Para começar a receber, conclua seu cadastro. Mas já pode criar e compartilhar seus eventos. 🎉';
  }
  if (!authStore.isFinanciallyEnabled) {
    return 'Cadastre sua chave Pix para começar a receber nas suas listas. 🔑';
  }
  if (loadingStats.value) return '';
  if (inProgressEvents.value.length === 0) {
    return 'Você ainda não tem eventos ativos. Que tal criar o primeiro? 🎉';
  }
  if (inProgressEvents.value.length === 1) {
    return 'Você tem 1 evento ativo. Compartilhe com seus convidados! 🎊';
  }
  return `Você tem ${inProgressEvents.value.length} eventos ativos. Acompanhe tudo aqui! 🚀`;
});

// ── Helpers ───────────────────────────────────────────────────
function toggleBalance() {
  isBalanceHidden.value = !isBalanceHidden.value;
  localStorage.setItem('isBalanceHidden', isBalanceHidden.value.toString());
}

function openPlatformOnboarding() {
  router.push(onboardingProfileLink.value);
}

// ── Ações ─────────────────────────────────────────────────────
const confirmLogout = async () => {
  isLoggingOut.value = true;
  try {
    await new Promise(r => setTimeout(r, 500));
    authStore.logout();
    router.push('/');
  } finally {
    isLoggingOut.value = false;
    showLogoutConfirmation.value = false;
  }
};

// ── Lifecycle ─────────────────────────────────────────────────
onMounted(async () => {
  loadingStats.value = true;

  // Só exibe o banner de onboarding após o sync de perfil concluir,
  // evitando flash do warning em contas já ativas.
  if (authStore.normalizedPlatformStatus !== 'active') {
    authStore.fetchUserProfile()
      .catch(() => {})
      .finally(() => { profileSynced.value = true; });
  } else {
    profileSynced.value = true;
  }

  try {
    const [summaryRes, eventsRes] = await Promise.allSettled([
      api.get('/users/finance/summary'),
      api.get('/events/in-progress,draft/status'),
    ]);

    if (summaryRes.status === 'fulfilled') {
      totalPaid.value = summaryRes.value.data?.totalReceived ?? summaryRes.value.data?.totalPaid ?? 0;
    }
    if (eventsRes.status === 'fulfilled') {
      allEvents.value = Array.isArray(eventsRes.value.data) ? eventsRes.value.data : [];
    }
  } catch {
    // silente — UI degrada graciosamente com zeros
  } finally {
    loadingStats.value = false;
    startTourIfFirstVisit();
  }
});
</script>

<style scoped>
/* Tour help button */
.dash-tour-btn {
  background: none;
  border: none;
  color: #ccc;
  font-size: 0.78rem;
  cursor: pointer;
  padding: 2px 4px;
  transition: color 0.15s;
  letter-spacing: 0.02em;
}
.dash-tour-btn:hover {
  color: #e91e8c;
}

/* ── Layout ───────────────────────────────────────── */
.dashboard-main-content {
  max-width: 700px;
  width: 100%;
  margin: 0 auto;
  padding: 1rem;
}
@media (min-width: 768px) {
  .dashboard-main-content { padding: 3rem; min-width: 600px; }
}

.text-primary-emphasis { color: #32355d !important; }
.context-message { font-size: 0.9rem; }
.text-pink { color: #ff50a3; }

/* ── Stat Cards ───────────────────────────────────── */
.stat-card {
  background: #fff;
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  cursor: pointer;
  transition: box-shadow 0.2s;
  position: relative;
}
.stat-card:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.12); }

.stat-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.stat-card__icon--green  { background: #e8f5e9; color: #4CAF50; }
.stat-card__icon--purple { background: #ede7f6; color: #7c4dff; }

.stat-card__body { flex-grow: 1; min-width: 0; }
.stat-card__label {
  font-size: 0.72rem;
  color: #6c757d;
  margin: 0;
}
.stat-card__value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #32355d;
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stat-card__toggle {
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  background: none;
  border: none;
  color: #adb5bd;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0.2rem;
}

/* ── Onboarding Banner ────────────────────────────── */
.onboarding-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-radius: 0.95rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.2s ease;
  box-shadow: 0 6px 16px rgba(50, 53, 93, 0.08);
}
.onboarding-banner:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(50, 53, 93, 0.12);
}
.onboarding-banner--warning {
  background: linear-gradient(135deg, #fff7e5 0%, #fff1cf 58%, #ffe9bf 100%);
  border: 1px solid #f6d58e;
}
.onboarding-banner--info {
  background: linear-gradient(135deg, #eef4ff 0%, #e5eeff 60%, #dde8ff 100%);
  border: 1px solid #c9d9ff;
}
.onboarding-banner--pix     { background: #e8f5e9; border: 1px solid #a5d6a7; }

.onboarding-banner__icon {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-size: 1rem;
  flex-shrink: 0;
  color: #b56a00;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(245, 124, 0, 0.2);
}
.onboarding-banner--info .onboarding-banner__icon {
  color: #2f6fd6;
  border-color: rgba(47, 111, 214, 0.2);
}
.onboarding-banner--pix .onboarding-banner__icon { color: #2e7d32; }
.onboarding-banner__content { flex-grow: 1; }
.onboarding-banner__title {
  font-weight: 700;
  font-size: 0.92rem;
  color: #2f3356;
  margin: 0 0 0.1rem;
}
.onboarding-banner__subtitle { font-size: 0.79rem; color: #5f6880; margin: 0; }
.onboarding-banner__arrow {
  color: #8a93aa;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(138, 147, 170, 0.22);
  border-radius: 999px;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  font-size: 0.75rem;
}

/* ── Onboarding Complete Banner ──────────────────── */
.onboarding-complete-banner {
  background: linear-gradient(135deg, #fff0f8, #e8f5e9);
  border: 1.5px solid #a5d6a7;
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  animation: slide-in 0.4s ease;
}
.onboarding-complete-banner__icon { font-size: 1.75rem; flex-shrink: 0; }
.onboarding-complete-banner__content { flex-grow: 1; }
.onboarding-complete-banner__title {
  font-weight: 700;
  font-size: 0.9rem;
  color: #2e7d32;
  margin: 0 0 0.15rem;
}
.onboarding-complete-banner__subtitle { font-size: 0.8rem; color: #555; margin: 0; }
.onboarding-complete-banner__cta {
  font-size: 0.82rem;
  font-weight: 700;
  color: #ff50a3;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}
@keyframes slide-in {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Section header ───────────────────────────────── */
.section-header { display: flex; justify-content: space-between; align-items: center; }
.section-title {
  font-weight: 700;
  font-size: 0.85rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}
.section-link { font-size: 0.82rem; color: #ff50a3; text-decoration: none; font-weight: 600; }

/* ── Event Mini Card ──────────────────────────────── */
.event-mini-card {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  background: #fff;
  border-radius: 0.875rem;
  padding: 0.62rem 0.7rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.event-mini-card:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.1); }

.event-mini-card__thumb {
  width: 50px;
  height: 50px;
  border-radius: 0.6rem;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0e6ff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.event-mini-card__thumb img { width: 100%; height: 100%; object-fit: cover; }
.event-mini-card__thumb-placeholder { color: #9c6bdc; font-size: 1.2rem; }
.event-mini-card__info { flex-grow: 1; min-width: 0; }
.event-mini-card__name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #32355d;
  margin: 0 0 0.22rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.event-mini-card__summary {
  background: #f8f9fa;
  border: 1px solid #eceff2;
  border-radius: 0.6rem;
  padding: 0.24rem 0.45rem;
}
.event-mini-card__stats-inline {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
}
.event-mini-card__meta {
  font-size: 0.78rem;
  margin: 0;
  color: #32355d;
  display: flex;
  align-items: center;
}
.event-mini-card__meta i {
  color: #ff50a3;
}
.event-mini-card__meta strong {
  font-weight: 700;
}
.event-mini-card__participants {
  font-size: 0.7rem;
  margin: 0;
  color: #6c757d;
  display: flex;
  align-items: center;
}
.event-mini-card__participants i {
  color: #9ea4ab;
}
.event-mini-card__arrow { font-size: 0.75rem; flex-shrink: 0; }

/* ── Empty hint ───────────────────────────────────── */
.empty-events-hint {
  background: #f8f9fa;
  border-radius: 0.75rem;
  padding: 1rem;
  text-align: center;
}

/* ── Action Buttons ───────────────────────────────── */
.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 1rem 0.5rem;
  border-radius: 1rem;
  font-weight: 700;
  font-size: 0.88rem;
  text-decoration: none;
  transition: transform 0.15s, box-shadow 0.15s;
  text-align: center;
  width: 100%;
}
.action-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.12); }
.action-btn i { font-size: 1.4rem; }
.action-btn--primary {
  background: linear-gradient(135deg, #ff50a3, #e6408f);
  color: #fff;
  box-shadow: 0 2px 8px rgba(255,80,163,0.35);
}
.action-btn--secondary {
  background: #fff;
  color: #32355d;
  border: 1.5px solid #e9ecef;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
}

/* ── Quick Cards ──────────────────────────────────── */
.quick-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 1rem 0.5rem;
  background: #fff;
  border-radius: 1rem;
  text-decoration: none;
  color: #32355d;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  position: relative;
  transition: box-shadow 0.2s;
  width: 100%;
}
.quick-card:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.12); color: #32355d; }
.quick-card__icon { font-size: 1.4rem; color: #ff50a3; }
.quick-card__label { line-height: 1.2; }
.quick-card__badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 0.65rem;
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.quick-card__badge--warn { background: #fff3cd; color: #856404; }
.quick-card__badge--ok   { background: #d1e7dd; color: #0a3622; }

/* ── Logout ───────────────────────────────────────── */
.btn-logout {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: background 0.15s;
}
.btn-logout:hover { background: #fff5f5; }

/* ── Draft cards ──────────────────────────────────── */
.stat-card--draft {
  opacity: 0.75;
}
.stat-card__icon--gray {
  background-color: #f0f0f0;
  color: #9e9e9e;
}
.event-card--draft {
  opacity: 0.6;
  border: 1.5px solid #d0d0d0 !important;
  cursor: pointer;
  filter: grayscale(30%);
  transition: opacity 0.2s;
}
.event-card--draft:hover {
  opacity: 0.8;
}
.event-card__badge {
  display: inline-block;
  font-size: 0.75rem;
  color: #9e9e9e;
  background: #f0f0f0;
  border-radius: 99px;
  padding: 2px 10px;
  margin-top: 4px;
}
.event-premium-crown {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #fff;
  font-size: 0.55rem;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  margin-right: 0.3rem;
  flex-shrink: 0;
  vertical-align: middle;
  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.4);
}
.section-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9e9e9e;
  font-weight: 700;
}

/* ── Draft modal ──────────────────────────────────── */
.draft-pending-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  margin-bottom: 10px;
}
.draft-pending-item__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff3e0;
  color: #ff9800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}
.draft-pending-item__content {
  flex: 1;
}
.draft-pending-item__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #32355d;
  margin: 0 0 2px;
}
.draft-pending-item__subtitle {
  font-size: 0.78rem;
  color: #9e9e9e;
  margin: 0;
}
.btn-outline-pink {
  border-color: #ff50a3;
  color: #ff50a3;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}
.btn-outline-pink:hover {
  background-color: #ff50a3;
  color: #fff;
}

/* ── Modal ────────────────────────────────────────── */
.confirmation-content h5 { font-size: 1.25rem; font-weight: 600; }
.confirmation-content p  { font-size: 0.95rem; }

.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}
.btn-color-site:hover:not(:disabled) {
  background-color: #e64593;
  border-color: #e64593;
  color: #fff;
}
.btn-color-site:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-outline-secondary {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #ced4da;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}
.btn-outline-secondary:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  color: #495057;
}

.spinner-border {
  vertical-align: -0.125em;
}

.stat-card__value--empty {
  font-size: 1.4rem;
  font-weight: 400;
  color: #c0c0c0;
  line-height: 1;
}

.help-hint {
  font-size: 0.78rem;
  color: #b0b0b0;
  text-align: center;
}

.help-hint__link {
  color: #ff50a3;
  text-decoration: none;
  font-weight: 600;
}

.help-hint__link:hover {
  text-decoration: underline;
}

@media (max-width: 767.98px) {
  .stat-card-col--receive,
  .stat-card-col--active {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
</style>
